import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { LEAD_STATUSES } from "../../data/content";

const FUNNEL = ["NEW", "CONTACTED", "ESTIMATE_SENT", "PAID", "IN_DEVELOPMENT", "HANDED_OVER"];

export default function Dashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((res) => setStats(res.data)).catch(() => {});
  }, []);

  if (!stats) {
    return <div className="w-8 h-8 border-2 border-flame border-t-transparent rounded-full animate-spin" data-testid="dashboard-loading" />;
  }

  const cards = [
    { label: "TOTAL LEADS", value: stats.total_leads, testid: "stat-total-leads" },
    { label: "NEW LEADS", value: stats.new_leads, testid: "stat-new-leads" },
    { label: "ESTIMATES SENT", value: stats.estimates_sent, testid: "stat-estimates-sent" },
    { label: "PAID / IN BUILD", value: stats.paid_projects, testid: "stat-paid" },
    { label: "RUSH HOUR BOOKINGS", value: stats.total_bookings, testid: "stat-bookings" },
    { label: "UPCOMING SESSIONS", value: stats.upcoming_bookings, testid: "stat-upcoming" },
    { label: "SPECIALIST SESSIONS", value: stats.specialist_bookings, testid: "stat-specialist" },
    { label: "SUPER VIBE SESSIONS", value: stats.super_bookings, testid: "stat-super" },
    { label: "CONVERSION RATE", value: `${stats.conversion_rate}%`, testid: "stat-conversion" },
  ];

  const maxFunnel = Math.max(1, ...FUNNEL.map((s) => stats.status_pipeline[s] || 0));

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-heading text-3xl font-extrabold text-white">Dashboard</h1>
      <p className="mt-2 text-sm text-slate-400">Pipeline, Rush Hour operations and conversion at a glance.</p>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-xl border border-white/10 bg-navy-800 p-5" data-testid={c.testid}>
            <p className="font-mono text-[9px] tracking-[0.2em] text-slate-500">{c.label}</p>
            <p className="mt-2 font-heading text-3xl font-black text-white">{c.value}</p>
          </div>
        ))}
        <div className="rounded-xl border border-flame/30 bg-flame/10 p-5" data-testid="stat-estimate-clicks">
          <p className="font-mono text-[9px] tracking-[0.2em] text-flame">ESTIMATE CLICKS</p>
          <p className="mt-2 font-heading text-3xl font-black text-white">{stats.events.estimate_click || 0}</p>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-white/10 bg-navy-800 p-7" data-testid="lead-funnel">
          <div className="flex items-center justify-between mb-6">
            <p className="font-heading font-bold text-white">Lead funnel</p>
            <Link to="/admin/leads" data-testid="funnel-view-leads" className="text-xs text-flame font-semibold hover:underline">View leads →</Link>
          </div>
          <div className="space-y-3">
            {FUNNEL.map((s) => (
              <div key={s} className="flex items-center gap-4">
                <span className="w-32 font-mono text-[10px] tracking-wider text-slate-400">{s.replace(/_/g, " ")}</span>
                <div className="flex-1 h-5 rounded bg-navy-900/70 overflow-hidden">
                  <div className="h-full bg-flame/80 rounded transition-all duration-700" style={{ width: `${((stats.status_pipeline[s] || 0) / maxFunnel) * 100}%` }} />
                </div>
                <span className="w-8 text-right font-mono text-xs text-white">{stats.status_pipeline[s] || 0}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-white/10 bg-navy-800 p-7" data-testid="engagement-stats">
          <p className="font-heading font-bold text-white mb-6">Engagement</p>
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: "RUSH HOUR CLICKS", v: stats.events.rush_hour_click || 0 },
              { label: "WHATSAPP CLICKS", v: stats.events.whatsapp_click || 0 },
              { label: "PHONE CLICKS", v: stats.events.phone_click || 0 },
              { label: "CALCULATOR USES", v: stats.events.calculator_use || 0 },
              { label: "PAGE VIEWS", v: stats.events.page_view || 0 },
              { label: "ESTIMATES SUBMITTED", v: stats.events.estimate_submitted || 0 },
            ].map((e) => (
              <div key={e.label} className="rounded-lg bg-navy-900/70 p-4">
                <p className="font-mono text-[9px] tracking-[0.15em] text-slate-500">{e.label}</p>
                <p className="mt-1.5 font-heading text-2xl font-bold text-white">{e.v}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-10 rounded-xl border border-white/10 bg-navy-800 p-7">
        <p className="font-heading font-bold text-white mb-5">Full pipeline</p>
        <div className="flex flex-wrap gap-2">
          {LEAD_STATUSES.map((s) => (
            <span key={s} data-testid={`pipeline-${s}`} className="rounded-full border border-white/10 bg-navy-900/60 px-4 py-2 font-mono text-[10px] tracking-wider text-slate-300">
              {s.replace(/_/g, " ")} · <span className="text-flame font-bold">{stats.status_pipeline[s] || 0}</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
